import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import callApi from 'client/util/apiCaller';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

const messages = defineMessages({
  title: {
    id: 'Billing.AddressDialog.Title',
    defaultMessage: 'Update billing address',
  },
  name: {
    id: 'Billing.AddressDialog.Name',
    defaultMessage: 'Name',
  },
  line1: {
    id: 'Billing.AddressDialog.Line1',
    defaultMessage: 'Address line 1',
  },
  line2: {
    id: 'Billing.AddressDialog.Line2',
    defaultMessage: 'Address line 2',
  },
  city: {
    id: 'Billing.AddressDialog.City',
    defaultMessage: 'City',
  },
  state: {
    id: 'Billing.AddressDialog.State',
    defaultMessage: 'State / Province',
  },
  postalCode: {
    id: 'Billing.AddressDialog.PostalCode',
    defaultMessage: 'Postal code',
  },
  country: {
    id: 'Billing.AddressDialog.Country',
    defaultMessage: 'Country (ISO code)',
  },
  save: {
    id: 'Billing.AddressDialog.Save',
    defaultMessage: 'Save address',
  },
  cancel: {
    id: 'Billing.AddressDialog.Cancel',
    defaultMessage: 'Cancel',
  },
  error: {
    id: 'Billing.AddressDialog.Error',
    defaultMessage: 'Could not update billing address. Please try again.',
  },
});

const emptyForm = {
  name: '',
  line1: '',
  line2: '',
  city: '',
  state: '',
  postal_code: '',
  country: '',
};

function UpdateBillingAddressDialog({ open, onClose, customer, onUpdated }) {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }
    const addr = customer?.billing_address || customer?.address || {};
    const name = [customer?.first_name, customer?.last_name].filter(Boolean).join(' ');
    setForm({
      name: name || '',
      line1: addr.line1 || '',
      line2: addr.line2 || '',
      city: addr.city || '',
      state: addr.state || '',
      postal_code: addr.postal_code || '',
      country: addr.country || '',
    });
    setError(false);
  }, [open, customer]);

  const updateField = field => event => {
    setForm(prev => ({ ...prev, [field]: event.target.value }));
  };

  const handleSave = async () => {
    setLoading(true);
    setError(false);
    try {
      const ret = await callApi(dispatch, 'users/subscriptions/billing-address', null, 'PUT', {
        name: form.name,
        address: {
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          postal_code: form.postal_code,
          country: form.country,
        },
      });
      onUpdated(ret);
      onClose();
    } catch (_err) {
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{formatMessage(messages.title)}</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
        {error && (
          <Typography variant="body2" color="error">
            {formatMessage(messages.error)}
          </Typography>
        )}
        <TextField
          label={formatMessage(messages.name)}
          value={form.name}
          onChange={updateField('name')}
          fullWidth
        />
        <TextField
          label={formatMessage(messages.line1)}
          value={form.line1}
          onChange={updateField('line1')}
          fullWidth
          required
        />
        <TextField
          label={formatMessage(messages.line2)}
          value={form.line2}
          onChange={updateField('line2')}
          fullWidth
        />
        <TextField
          label={formatMessage(messages.city)}
          value={form.city}
          onChange={updateField('city')}
          fullWidth
          required
        />
        <TextField
          label={formatMessage(messages.state)}
          value={form.state}
          onChange={updateField('state')}
          fullWidth
        />
        <TextField
          label={formatMessage(messages.postalCode)}
          value={form.postal_code}
          onChange={updateField('postal_code')}
          fullWidth
          required
        />
        <TextField
          label={formatMessage(messages.country)}
          value={form.country}
          onChange={updateField('country')}
          fullWidth
          required
          inputProps={{ maxLength: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          {formatMessage(messages.cancel)}
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={loading}>
          {formatMessage(messages.save)}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

UpdateBillingAddressDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  customer: PropTypes.object,
  onUpdated: PropTypes.func.isRequired,
};

UpdateBillingAddressDialog.defaultProps = {
  customer: null,
};

export default UpdateBillingAddressDialog;
